import { Button } from "@shared/components/ui/Button";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import theme from "../../../shared/utils/themeColors";
import OnboardingLayout from "../components/OnboardingLayout";
import { useOnboarding } from "../hooks/useOnboarding";
import { OnboardingStep, OrganizationInfo, OrganizationType } from "../types";

/**
 * Second step of onboarding - Organization information
 */
const Onboarding02Page: React.FC = () => {
  const {
    organizationType,
    organizationInfo,
    loading,
    error,
    saveOrganizationInfo,
    setError,
    isInvitedUser,
  } = useOnboarding();

  const navigate = useNavigate();

  // Initialize form state with existing data or defaults
  const [formData, setFormData] = useState<OrganizationInfo>({
    name: organizationInfo?.name || "",
    city: organizationInfo?.city || "",
    postalCode: organizationInfo?.postalCode || "",
    streetAddress: organizationInfo?.streetAddress || "",
    country: organizationInfo?.country || "United States",
    state: organizationInfo?.state || "",
    website: organizationInfo?.website || "",
    size: organizationInfo?.size || "",
    industry: organizationInfo?.industry || "",
  });

  useEffect(() => {
    if (organizationInfo) {
      setFormData({
        name: organizationInfo.name || "",
        city: organizationInfo.city || "",
        postalCode: organizationInfo.postalCode || "",
        streetAddress: organizationInfo.streetAddress || "",
        country: organizationInfo.country || "United States",
        state: organizationInfo.state || "",
        website: organizationInfo.website || "",
        size: organizationInfo.size || "",
        industry: organizationInfo.industry || "",
      });
    }
  }, [organizationInfo]);

  // Determine if this is an organization or corporate member
  const isOrganization = organizationType === OrganizationType.ORGANIZATION;

  // List of countries for the dropdown
  const countries = [
    "United States",
    "Canada",
    "Mexico",
    "Brazil",
    "Argentina",
    "Colombia",
    "Chile",
    "Peru",
    "United Kingdom",
    "Germany",
    "France",
    "Spain",
    "Italy",
    "Other",
  ];

  // List of US states for the dropdown
  const usStates = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ];

  // List of Canadian provinces for the dropdown
  const canadianProvinces = [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Northwest Territories",
    "Nova Scotia",
    "Nunavut",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Yukon",
  ];

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name) {
      setError(
        isOrganization
          ? "Organization name is required"
          : "Company name is required"
      );
      return;
    }

    if (!formData.country) {
      setError("Country is required");
      return;
    }

    // US and Canada require state/province
    if (
      (formData.country === "United States" || formData.country === "Canada") &&
      !formData.state
    ) {
      setError(
        `${
          formData.country === "United States" ? "State" : "Province"
        } is required`
      );
      return;
    }

    // For organizations, industry is required
    if (isOrganization && !formData.industry) {
      setError("Industry is required for organizations");
      return;
    }

    saveOrganizationInfo(formData);
  };

  // Get the appropriate state/province label based on country
  const getStateLabel = () => {
    if (formData.country === "United States") return "State";
    if (formData.country === "Canada") return "Province";
    return "State/Province";
  };

  // Get the appropriate state/province options based on country
  const getStateOptions = () => {
    if (formData.country === "United States") return usStates;
    if (formData.country === "Canada") return canadianProvinces;
    return [];
  };

  return (
    <OnboardingLayout currentStep={OnboardingStep.ORGANIZATION_INFO}>
      <h1 className={`text-3xl ${theme.text.primary} mb-6`}>
        {isInvitedUser
          ? "Your Organization"
          : isOrganization
          ? "Tell us about your organization"
          : "Tell us about your company"}
      </h1>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}

      {isInvitedUser ? (
        <div className="mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700/60 shadow-sm">
            <div className="flex items-center justify-center mb-4">
              <svg
                className={`fill-current ${theme.text.primary}`}
                xmlns="http://www.w3.org/2000/svg"
                width={48}
                height={48}
                viewBox="0 0 24 24"
              >
                <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
              </svg>
            </div>

            <h2
              className={`text-xl ${theme.text.primary} font-medium text-center mb-2`}
            >
              {organizationInfo?.name || "Your Organization"}
            </h2>

            <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
              You've been invited to join this organization. You'll be able to
              access all organization features after completing your profile.
            </p>

            <div className="mt-6 flex justify-center">
              <Button
                type="button"
                onClick={() => navigate("/onboarding-3")}
                variant="primary"
              >
                Continue to Profile Setup →
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className={`block text-sm font-medium ${theme.text.primary} mb-1`}
            >
              {isOrganization ? "Organization Name *" : "Company Name *"}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
              placeholder={
                isOrganization
                  ? "Enter organization name"
                  : "Enter company name"
              }
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="country"
                className={`block text-sm font-medium ${theme.text.primary} mb-1`}
              >
                Country *
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
                required
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            {(formData.country === "United States" ||
              formData.country === "Canada") && (
              <div>
                <label
                  htmlFor="state"
                  className={`block text-sm font-medium ${theme.text.primary} mb-1`}
                >
                  {getStateLabel()} *
                </label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
                  required
                >
                  <option value="">Select {getStateLabel()}</option>
                  {getStateOptions().map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formData.country !== "United States" &&
              formData.country !== "Canada" && (
                <div>
                  <label
                    htmlFor="state"
                    className={`block text-sm font-medium ${theme.text.primary} mb-1`}
                  >
                    {getStateLabel()}
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
                    placeholder={`Enter ${getStateLabel()}`}
                  />
                </div>
              )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="city"
                className={`block text-sm font-medium ${theme.text.primary} mb-1`}
              >
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
                placeholder="Enter city"
              />
            </div>

            <div>
              <label
                htmlFor="postalCode"
                className={`block text-sm font-medium ${theme.text.primary} mb-1`}
              >
                Postal Code
              </label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
                placeholder="Enter postal code"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="streetAddress"
              className={`block text-sm font-medium ${theme.text.primary} mb-1`}
            >
              Street Address
            </label>
            <input
              type="text"
              id="streetAddress"
              name="streetAddress"
              value={formData.streetAddress}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
              placeholder="Enter street address"
            />
          </div>

          <div>
            <label
              htmlFor="website"
              className={`block text-sm font-medium ${theme.text.primary} mb-1`}
            >
              Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
              placeholder="https://example.com"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="size"
                className={`block text-sm font-medium ${theme.text.primary} mb-1`}
              >
                {isOrganization ? "Organization Size" : "Company Size"}
              </label>
              <select
                id="size"
                name="size"
                value={formData.size}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
              >
                <option value="">Select size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1001+">1001+ employees</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="industry"
                className={`block text-sm font-medium ${theme.text.primary} mb-1`}
              >
                Industry {isOrganization && "*"}
              </label>
              <select
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
                required={isOrganization}
              >
                <option value="">Select industry</option>
                <option value="Agriculture">Agriculture</option>
                <option value="Construction">Construction</option>
                <option value="Education">Education</option>
                <option value="Energy">Energy</option>
                <option value="Finance">Finance</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Retail">Retail</option>
                <option value="Technology">Technology</option>
                <option value="Transportation">Transportation</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              onClick={() => navigate("/onboarding-1")}
              variant="secondary"
            >
              ← Back
            </Button>

            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Saving..." : "Next Step →"}
            </Button>
          </div>
        </form>
      )}
    </OnboardingLayout>
  );
};

export default Onboarding02Page;
